import {
  FORM_CONSTANTS,
  PHONE_CONSTANTS,
  AGE_CONSTANTS,
} from '../config/constants';

// ✅ INTERFACE PARA RESULTADO DE VALIDACIÓN
export interface ValidationResult {
  isValid: boolean;
  errorMessage: string;
}

// ✅ VALIDACIONES BÁSICAS
export const validators = {
  // ✅ VALIDACIÓN DE RUT
  rut: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'RUT es requerido' };
    }

    if (value.length < FORM_CONSTANTS.MIN_RUT_LENGTH) {
      return {
        isValid: false,
        errorMessage: `RUT debe tener al menos ${FORM_CONSTANTS.MIN_RUT_LENGTH} caracteres`,
      };
    }

    if (value.length > FORM_CONSTANTS.MAX_RUT_LENGTH) {
      return {
        isValid: false,
        errorMessage: `RUT no puede exceder ${FORM_CONSTANTS.MAX_RUT_LENGTH} caracteres`,
      };
    }

    // ✅ VALIDACIÓN DE FORMATO DE RUT CHILENO
    const rutPattern = /^[0-9]{7,8}-[0-9kK]$/;
    if (!rutPattern.test(value)) {
      return {
        isValid: false,
        errorMessage: 'Formato de RUT inválido (ej: 12345678-9)',
      };
    }

    // ✅ VALIDACIÓN DE DÍGITO VERIFICADOR
    const rut = value.replace(/[.-]/g, '');
    const dv = rut.slice(-1);
    let rutNumber = parseInt(rut.slice(0, -1));

    let M = 0;
    let S = 1;
    // eslint-disable-next-line no-param-reassign
    for (; rutNumber; rutNumber = Math.floor(rutNumber / 10)) {
      S = (S + (rutNumber % 10) * (9 - (M % 6))) % 11;
      M += 1;
    }

    const calculatedDv = S ? S - 1 : 'k';

    if (dv.toLowerCase() !== calculatedDv.toString().toLowerCase()) {
      return { isValid: false, errorMessage: 'Dígito verificador inválido' };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE CONTRASEÑA
  password: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'Contraseña es requerida' };
    }

    if (value.length < FORM_CONSTANTS.MIN_PASSWORD_LENGTH) {
      return {
        isValid: false,
        errorMessage: `La contraseña debe tener al menos ${FORM_CONSTANTS.MIN_PASSWORD_LENGTH} caracteres`,
      };
    }

    if (value.length > FORM_CONSTANTS.MAX_PASSWORD_LENGTH) {
      return {
        isValid: false,
        errorMessage: `La contraseña no puede exceder ${FORM_CONSTANTS.MAX_PASSWORD_LENGTH} caracteres`,
      };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE CONFIRMACIÓN DE CONTRASEÑA
  passwordConfirmation: (
    password: string,
    confirmation: string
  ): ValidationResult => {
    if (!confirmation) {
      return { isValid: false, errorMessage: 'Confirma tu contraseña' };
    }

    if (password !== confirmation) {
      return { isValid: false, errorMessage: 'Las contraseñas no coinciden' };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE TELÉFONO
  phone: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'Teléfono es requerido' };
    }

    if (value.length !== FORM_CONSTANTS.PHONE_LENGTH) {
      return {
        isValid: false,
        errorMessage: `El teléfono debe tener ${FORM_CONSTANTS.PHONE_LENGTH} dígitos`,
      };
    }

    if (!PHONE_CONSTANTS.FORMAT_PATTERN.test(value)) {
      return { isValid: false, errorMessage: 'Formato de teléfono inválido' };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE NOMBRE
  name: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'Nombre es requerido' };
    }

    if (value.length < FORM_CONSTANTS.MIN_NAME_LENGTH) {
      return {
        isValid: false,
        errorMessage: `El nombre debe tener al menos ${FORM_CONSTANTS.MIN_NAME_LENGTH} caracteres`,
      };
    }

    if (value.length > FORM_CONSTANTS.MAX_NAME_LENGTH) {
      return {
        isValid: false,
        errorMessage: `El nombre no puede exceder ${FORM_CONSTANTS.MAX_NAME_LENGTH} caracteres`,
      };
    }

    // ✅ VALIDAR QUE SOLO CONTENGA LETRAS Y ESPACIOS
    const namePattern = /^[a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+$/;
    if (!namePattern.test(value)) {
      return {
        isValid: false,
        errorMessage: 'El nombre solo puede contener letras y espacios',
      };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE ALTURA
  height: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'Altura es requerida' };
    }

    const height = parseFloat(value);
    if (isNaN(height)) {
      return {
        isValid: false,
        errorMessage: 'Altura debe ser un número válido',
      };
    }

    if (
      height < FORM_CONSTANTS.MIN_HEIGHT ||
      height > FORM_CONSTANTS.MAX_HEIGHT
    ) {
      return {
        isValid: false,
        errorMessage: `La altura debe estar entre ${FORM_CONSTANTS.MIN_HEIGHT} y ${FORM_CONSTANTS.MAX_HEIGHT} cm`,
      };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE PESO
  weight: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'Peso es requerido' };
    }

    const weight = parseFloat(value);
    if (isNaN(weight)) {
      return { isValid: false, errorMessage: 'Peso debe ser un número válido' };
    }

    if (
      weight < FORM_CONSTANTS.MIN_WEIGHT ||
      weight > FORM_CONSTANTS.MAX_WEIGHT
    ) {
      return {
        isValid: false,
        errorMessage: `El peso debe estar entre ${FORM_CONSTANTS.MIN_WEIGHT} y ${FORM_CONSTANTS.MAX_WEIGHT} kg`,
      };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE FECHA DE NACIMIENTO
  birthday: (value: string): ValidationResult => {
    if (!value) {
      return {
        isValid: false,
        errorMessage: 'Fecha de nacimiento es requerida',
      };
    }

    const birthDate = new Date(value);
    const today = new Date();

    if (isNaN(birthDate.getTime())) {
      return { isValid: false, errorMessage: 'Fecha inválida' };
    }

    if (birthDate > today) {
      return {
        isValid: false,
        errorMessage: 'La fecha de nacimiento no puede ser futura',
      };
    }

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age -= 1;
    }

    if (age < AGE_CONSTANTS.MIN_AGE) {
      return {
        isValid: false,
        errorMessage: `Debes tener al menos ${AGE_CONSTANTS.MIN_AGE} años`,
      };
    }

    if (age > AGE_CONSTANTS.MAX_AGE) {
      return {
        isValid: false,
        errorMessage: `La edad no puede exceder ${AGE_CONSTANTS.MAX_AGE} años`,
      };
    }

    return { isValid: true, errorMessage: '' };
  },

  // ✅ VALIDACIÓN DE EMAIL
  email: (value: string): ValidationResult => {
    if (!value) {
      return { isValid: false, errorMessage: 'Email es requerido' };
    }

    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(value)) {
      return { isValid: false, errorMessage: 'Formato de email inválido' };
    }

    return { isValid: true, errorMessage: '' };
  },
};

// ✅ FUNCIÓN DE VALIDACIÓN COMPLETA DEL FORMULARIO
export const validateForm = (
  formData: Record<string, any>
): Record<string, string> => {
  const errors: Record<string, string> = {};

  // ✅ VALIDAR CAMPOS REQUERIDOS
  Object.keys(formData).forEach(field => {
    if (
      formData[field] === '' ||
      formData[field] === null ||
      formData[field] === undefined
    ) {
      errors[field] = 'Este campo es requerido';
    }
  });

  return errors;
};

// ✅ FUNCIÓN PARA LIMPIAR ERRORES
export const clearErrors = (): Record<string, string> => {
  return {};
};

export default validators;
